import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CorporateAccountOpenProfileDetailsPages, useCorporateAccountOpenProfileDetailsPageTypeStore } from 'views/pages/accountOpenProfileDetails/corporateProfileDetails/hooks';
import { useEffect } from 'react';
import { useCorporateAccountEstablishStore } from '../hooks';
import { ErrorWord, getMaxLengthErrorMessage, TemplateProps, yapStringWhen } from '../common';
import { CheckBoxItemType } from 'views/templates/module/checkInput';
import { DomesticRealEstatePurpose, DomesticRealEstatePurposeStrs, LeaseFundPurpose, CorporateLeaseFundPurposeStrs, CorporateOverseasRealEstatePurpose, CorporateOverseasRealEstatePurposeStrs } from 'views/utils/enums';
import { formErrorScroll } from 'views/mutual/formErrorScroll';

export type PurposeState={
  isAnyPurposeSelected: boolean;
  leaseFundChecked : boolean;
  domesticRealEstateChecked : boolean;
  overseasRealEstateChecked : boolean;
}
export type TransactionPurposeType = {
  leaseFundPurpose: CheckBoxItemType[];
  leaseFundPurposeOtherText: string;
  domesticRealEstatePurpose: CheckBoxItemType[];
  domesticRealEstatePurposeOtherText: string;
  overseasRealEstatePurpose: CheckBoxItemType[];
  overseasRealEstatePurposeOtherText: string;
};

export const useTransactionPurpose = ({ isUpdate }: TemplateProps) => {
  const { param, add } = useCorporateAccountEstablishStore();
  const { setAccountOpenProfileDetailsPageType } = useCorporateAccountOpenProfileDetailsPageTypeStore();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const purposeArraySchema = Yup.object().shape({
    label: Yup.string(),
    checked: Yup.boolean(),
  });
  const validation = Yup.object().shape({
    isAnyPurposeSelected: Yup.boolean()
      .test('ArrayCheck', ErrorWord.SELECT_ONE_OR_MORE, function () {
        const parent = this.parent as TransactionPurposeType & PurposeState;
        return parent.leaseFundChecked || parent.domesticRealEstateChecked || parent.overseasRealEstateChecked;
      }),
    leaseFundPurpose: Yup.array()
      .of(purposeArraySchema)
      .test('ArrayCheck', ErrorWord.SELECT_ONE_OR_MORE, function (list) {
        const parent = this.parent as TransactionPurposeType & PurposeState;
        if (!parent.leaseFundChecked) return true;
        return list?.some((item) => item.checked);
      }),
    leaseFundPurposeOtherText: yapStringWhen(
      'leaseFundPurpose',
      (val: CheckBoxItemType[]) => val.filter((x) => x.checked).some((x) => x.label === LeaseFundPurpose.Other),
      ErrorWord.INPUT,
    ).max(25, getMaxLengthErrorMessage(25)),
    domesticRealEstatePurpose: Yup.array()
      .of(purposeArraySchema)    
      .test('ArrayCheck', ErrorWord.SELECT_ONE_OR_MORE, function (list) {
        const parent = this.parent as TransactionPurposeType & PurposeState;
        if (!parent.domesticRealEstateChecked) return true;
        return list?.some((item) => item.checked);
      }),
    domesticRealEstatePurposeOtherText: yapStringWhen(
      'domesticRealEstatePurpose',
      (val: CheckBoxItemType[]) => val.filter((x) => x.checked).some((x) => x.label === DomesticRealEstatePurpose.Other),
      ErrorWord.INPUT,
    ).max(25, getMaxLengthErrorMessage(25)),
    overseasRealEstatePurpose: Yup.array()
      .of(purposeArraySchema)
      .test('ArrayCheck', ErrorWord.SELECT_ONE_OR_MORE, function (list) {
        const parent = this.parent as TransactionPurposeType & PurposeState;
        if (!parent.overseasRealEstateChecked) return true;
        return list?.some((item) => item.checked);
      }),
    overseasRealEstatePurposeOtherText: yapStringWhen(
      'overseasRealEstatePurpose',
      (val: CheckBoxItemType[]) => val.filter((x) => x.checked).some((x) => x.label === CorporateOverseasRealEstatePurpose.Other),
      ErrorWord.INPUT,
    ).max(25, getMaxLengthErrorMessage(25)),
  });

  const initialValues: TransactionPurposeType & PurposeState = {
    isAnyPurposeSelected: 
    param.leaseFundPurpose?.map((x) => x.checked).includes(true) || 
    param.domesticRealEstatePurpose?.map((x) => x.checked).includes(true) ||
    param.overseasRealEstatePurpose?.map((x) => x.checked).includes(true),
    leaseFundChecked: param.leaseFundPurpose?.map((x) => x.checked).includes(true),
    leaseFundPurpose: CorporateLeaseFundPurposeStrs.map<CheckBoxItemType>((data) => {
      return { label: data, checked: param.leaseFundPurpose?.find((x) => x.label === data)?.checked ?? false };
    }),
    leaseFundPurposeOtherText: param.leaseFundPurposeOtherText ?? '',
    domesticRealEstateChecked: param.domesticRealEstatePurpose?.map((x) => x.checked).includes(true),
    domesticRealEstatePurpose: DomesticRealEstatePurposeStrs.filter((x) => x === DomesticRealEstatePurpose.ShisanUnyo || x === DomesticRealEstatePurpose.Other).map<CheckBoxItemType>((data) => {
      return { label: data, checked: param.domesticRealEstatePurpose?.find((x) => x.label === data)?.checked ?? false };
    }),
    domesticRealEstatePurposeOtherText: param.domesticRealEstatePurposeOtherText ?? '',
    overseasRealEstateChecked : param.overseasRealEstatePurpose?.map((x) => x.checked).includes(true),
    overseasRealEstatePurpose: CorporateOverseasRealEstatePurposeStrs.map<CheckBoxItemType>((data) => {
      return { label: data, checked: param.overseasRealEstatePurpose?.find((x) => x.label === data)?.checked ?? false };
    }),
    overseasRealEstatePurposeOtherText: param.overseasRealEstatePurposeOtherText ?? '',
  };

  const formik = useFormik<TransactionPurposeType & PurposeState>({
    enableReinitialize: isUpdate,
    initialValues,
    async onSubmit() {
      add({
        ...param,
        ...formik.values,
      });
      setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.contactInfo);
    },
    validationSchema: validation,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const isAnyPurposeSelectedProps = {
    leaseFund: {
      name : "リースファンド",
      setChecked: ()=> {
        //ON→OFFの場合初期化
        if(formik.values.leaseFundChecked){
          formik.setFieldValue('leaseFundPurpose', CorporateLeaseFundPurposeStrs.map<CheckBoxItemType>((data) => {
            return { label: data, checked: false };
          }));
          formik.setFieldValue('leaseFundPurposeOtherText', '');
        }
        formik.setFieldValue('leaseFundChecked', !formik.values.leaseFundChecked)      
      },
      checked: formik.values.leaseFundChecked,
    },
    domesticRealEstate: {
      name : "国内不動産",
      setChecked: ()=> {
        //ON→OFFの場合初期化
        if(formik.values.domesticRealEstateChecked){
          formik.setFieldValue('domesticRealEstatePurpose', DomesticRealEstatePurposeStrs.filter((x) => x === DomesticRealEstatePurpose.ShisanUnyo || x === DomesticRealEstatePurpose.Other).map<CheckBoxItemType>((data) => {
            return { label: data, checked: false };
          }));
          formik.setFieldValue('domesticRealEstatePurposeOtherText', '');
        }      
        formik.setFieldValue('domesticRealEstateChecked', !formik.values.domesticRealEstateChecked)
      },
      checked: formik.values.domesticRealEstateChecked,
    },
    overseasRealEstate: {
      name : "海外不動産",
      setChecked: ()=> {
        if(formik.values.overseasRealEstateChecked){
          formik.setFieldValue('overseasRealEstatePurpose', CorporateOverseasRealEstatePurposeStrs.map<CheckBoxItemType>((data) => {
            return { label: data, checked: false };
          }));
          formik.setFieldValue('overseasRealEstatePurposeOtherText', '');
        }
        formik.setFieldValue('overseasRealEstateChecked', !formik.values.overseasRealEstateChecked)
      },
      checked: formik.values.overseasRealEstateChecked,
    },
    error: formik.errors.isAnyPurposeSelected,
  }

  const leaseFundPurposeProps = {
    //チェックボックス
    multiCheckInput : {
      items: formik.values.leaseFundPurpose,
      onChangeItems: (items: CheckBoxItemType[]) => {
        if (!items.some((x) => x.checked && x.label === LeaseFundPurpose.Other)) {
          formik.setFieldValue('leaseFundPurposeOtherText', '');
        }
        formik.setFieldValue('leaseFundPurpose', items);
      },
      name: 'リースファンド',
      error: formik.errors.leaseFundPurpose as string,
    },
    //その他テキスト
    otherText: formik.values.leaseFundPurpose.filter((x) => x.checked).some((x) => x.label === LeaseFundPurpose.Other) && {
      value: formik.values.leaseFundPurposeOtherText,
      onChange: (value: string) => formik.setFieldValue('leaseFundPurposeOtherText', value),
      error: formik.errors.leaseFundPurposeOtherText,
    },
  };
  const domesticRealEstatePurposeProps = {
    //チェックボックス
    multiCheckInput: {
      items: formik.values.domesticRealEstatePurpose,
      onChangeItems: (items: CheckBoxItemType[]) => {
        if (!items.some((x) => x.checked && x.label === DomesticRealEstatePurpose.Other)) {
          formik.setFieldValue('domesticRealEstatePurposeOtherText', '');
        }
        formik.setFieldValue('domesticRealEstatePurpose', items);
      },
      name: '国内不動産',
      error: formik.errors.domesticRealEstatePurpose as string,
    },    
    otherText: formik.values.domesticRealEstatePurpose.filter((x) => x.checked).some((x) => x.label === DomesticRealEstatePurpose.Other) && {
      value: formik.values.domesticRealEstatePurposeOtherText,
      onChange: (value: string) => formik.setFieldValue('domesticRealEstatePurposeOtherText', value),
      error: formik.errors.domesticRealEstatePurposeOtherText,
    },
  };
  const overseasRealEstatePurposeProps = {
    //チェックボックス    
    multiCheckInput : {
      items: formik.values.overseasRealEstatePurpose,
      onChangeItems: (items: CheckBoxItemType[]) => {
        if (!items.some((x) => x.checked && x.label === CorporateOverseasRealEstatePurpose.Other)) {
          formik.setFieldValue('overseasRealEstatePurposeOtherText', '');
        }
        formik.setFieldValue('overseasRealEstatePurpose', items);
      },
      name: '海外不動産',
      error: formik.errors.overseasRealEstatePurpose as string,
    },
    otherText: formik.values.overseasRealEstatePurpose.filter((x) => x.checked).some((x) => x.label === CorporateOverseasRealEstatePurpose.Other) && {
      value: formik.values.overseasRealEstatePurposeOtherText,
      onChange: (value: string) => formik.setFieldValue('overseasRealEstatePurposeOtherText', value),
      error: formik.errors.overseasRealEstatePurposeOtherText,
    },
  };

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    formik,
    leaseFundPurposeProps,
    domesticRealEstatePurposeProps,
    overseasRealEstatePurposeProps,
    isAnyPurposeSelectedProps,
  };
};
